import React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Div from '../Div';

export default function VideoModal({ videoSrc, bgUrl, variant }) {
  const [iframeSrc, setIframeSrc] = useState('about:blank');
  const [toggle, setToggle] = useState(false);
  const videoRef = useRef(null);

  // Detectar se é vídeo local ou YouTube
  const isLocalVideo = videoSrc && (
    videoSrc.endsWith('.mp4') || 
    videoSrc.endsWith('.mov') || 
    videoSrc.endsWith('.webm') ||
    videoSrc.endsWith('.ogg')
  );

  // Função para fechar o modal
  const handelClose = useCallback(() => {
    if (isLocalVideo) {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    } else {
      setIframeSrc('about:blank');
    }
    setToggle(false);
  }, [isLocalVideo]);

  // Bloquear scroll do body quando modal está aberto
  useEffect(() => {
    if (toggle) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [toggle]);

  // Fechar com tecla ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && toggle) {
        handelClose();
      }
    };

    if (toggle) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle, handelClose]);

  const handelClick = () => {
    if (isLocalVideo) {
      setToggle(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      }, 100);
    } else {
      const video = videoSrc.split('?v=')[1]?.trim();
      if (video) {
        setIframeSrc(`https://www.youtube.com/embed/${video}?autoplay=1`);
      }
      setToggle(true);
    }
  };

  // Componente do Modal renderizado via Portal
  const ModalContent = () => (
    <div className="cs-video_popup active">
      <div className="cs-video_popup_overlay" onClick={handelClose} />
      <div className="cs-video_popup_content">
        <div className="cs-video_popup_container">
          <button 
            className="cs-video_popup_close" 
            onClick={handelClose}
            aria-label="Fechar vídeo"
          />
          <div className="cs-video_popup_align">
            <div className="embed-responsive embed-responsive-9by16">
              {isLocalVideo ? (
                <video
                  ref={videoRef}
                  className="embed-responsive-item"
                  controls
                  playsInline
                  autoPlay
                >
                  <source 
                    src={videoSrc} 
                    type={videoSrc.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'} 
                  />
                  Seu navegador não suporta vídeos HTML5.
                </video>
              ) : (
                <iframe
                  className="embed-responsive-item"
                  src={iframeSrc}
                  title="video modal"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Thumbnail / Card de Vídeo */}
      <Div
        className={`cs-video_block ${variant ? variant : 'cs-style1'} cs-video_open cs-bg`}
        style={{ backgroundImage: `url(${bgUrl})` }}
        onClick={handelClick}
      >
        <span className="cs-player_btn cs-accent_color">
          <span />
        </span>
      </Div>

      {/* Modal renderizado via Portal no body */}
      {toggle && createPortal(<ModalContent />, document.body)}
    </>
  );
}
